import { motion } from "framer-motion";
import { Stethoscope, User, FileText, Pill, TrendingUp } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";
import { imgFallback } from "../../utils";
import { HEADSHOT_FALLBACK } from "../../data/content";

const CHAIN = [
  { icon: Stethoscope, label: "Doctor" },
  { icon: User, label: "Patient" },
  { icon: FileText, label: "Prescription" },
  { icon: Pill, label: "Product" },
  { icon: TrendingUp, label: "Market" },
];

export const ChapterOne = () => (
  <ChapterShell
    n="01"
    id="chapter-1"
    era="2008 · CIPLA"
    title="The Beginning"
    quote="Every great marketing career starts by understanding the real world."
  >
    <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <FadeUp className="glass inline-flex flex-wrap items-center gap-4 rounded-2xl px-6 py-4">
          <span className="font-display text-3xl font-black text-[#0A192F]">2008</span>
          <span className="h-8 w-px bg-[#0A192F]/15" />
          <span>
            <span className="block font-display text-sm font-bold uppercase tracking-[0.15em] text-[#0A192F]">
              CIPLA — Medical Representative
            </span>
            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#0A192F]/50">
              Jul 2008 – Mar 2009
            </span>
          </span>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-8 max-w-xl text-base leading-relaxed text-[#4A5568] md:text-lg">
          The journey began close to doctors, patients and the field — where every prescription is a decision, and
          every decision is a lesson in how markets truly move.
        </FadeUp>

        <FadeUp delay={0.25} className="relative mt-12">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-6 right-6 top-7 hidden h-px origin-left bg-gradient-to-r from-[#00BFA5] via-[#00E5FF] to-[#0A192F] md:block"
          />
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-5">
            {CHAIN.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: 0.35 + i * 0.14 }}
                className="flex flex-col items-center gap-3 text-center"
              >
                <span className="glass flex h-14 w-14 items-center justify-center rounded-2xl text-[#0A192F] transition-transform duration-300 hover:-translate-y-1">
                  <c.icon size={22} />
                </span>
                <span className="font-display text-[11px] font-bold uppercase tracking-[0.2em] text-[#0A192F]/70">
                  {c.label}
                </span>
              </motion.div>
            ))}
          </div>
        </FadeUp>
      </div>

      <div className="lg:col-span-5">
        <FadeUp delay={0.2} className="relative mx-auto max-w-xs">
          <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-[#00BFA5]/20 to-[#00E5FF]/20 blur-xl" />
          <div className="glass relative overflow-hidden rounded-[1.8rem] p-2">
            <img
              src="/assets/avatar-walk.png"
              onError={imgFallback(HEADSHOT_FALLBACK)}
              alt="Rishikesh walking through the field"
              data-testid="chapter1-avatar"
              className="w-full rounded-[1.4rem] object-cover"
            />
          </div>
          <p className="mt-5 text-center text-[11px] font-bold uppercase tracking-[0.25em] text-[#0A192F]/45">
            First steps — the field years
          </p>
        </FadeUp>
      </div>
    </div>
  </ChapterShell>
);
